/**
 * Kelly's surface-scoped analytics properties, in the panel-scoped form ANALYTICS_ARCHITECTURE.md describes
 * (`use<X>AnalyticsProperties`): read straight from the slice + the router, memoized on primitives.
 */

import { useMemo } from "react"
import { useLocation, useMatches } from "react-router-dom"
import { useAppSelector } from "hooks/redux"
import type { TrackingProperties } from "connected-components/analytics/context"
import { selectDockView } from "../store/agent-chat-slice"
import type { DockView } from "../store/agent-chat-slice"
import { routeTemplate } from "./route-template"

export interface KellyAnalyticsProperties extends TrackingProperties {
  /** Which view Kelly is in when the event fires — the dock's whole premise is `docked`, so it is measured. */
  dockView: DockView
  /** The page Kelly is open beside, as a low-cardinality template (see route-template.ts). */
  route: string
}

export function useKellyAnalyticsProperties(): KellyAnalyticsProperties {
  const dockView = useAppSelector(selectDockView)
  const { pathname } = useLocation()
  // The deepest match carries every parameter the branch filled in — this hook sits above the account Outlet,
  // so `useParams()` here would see only the alias and leave a leaf's parameters to be guessed by shape.
  const matches = useMatches()
  const route = routeTemplate(pathname, matches[matches.length - 1]?.params)

  // Both are primitives, so the object stays referentially stable across unrelated renders — a fresh one
  // would rebuild every consumer's `track` and re-run the effects that depend on it.
  return useMemo(() => ({ dockView, route }), [dockView, route])
}
